import type { ContentInsertPosition } from "@appEnum/ContentInsertPosition";
import type { ElementSelectorStrategy } from "@interfaces/dom/IElementSelector";

export const DESIGNER_MESSAGE_SOURCE = "s1-content-designer" as const;
export const DESIGNER_PROTOCOL_VERSION = 1 as const;
export const DESIGNER_QUERY_FLAG = "s1ContentDesigner";

/**
 * Single-use nonce the designer puts on the frame URL. The server trades it for the session cookies
 * and redirects it back off the address, so no credential and no origin ever survives in the URL.
 */
export const DESIGNER_HANDOFF_PARAM = "s1ContEditHandoff";
export const DESIGNER_CHROME_CLASS = "s1-content-designer-chrome";

/**
 * Cookie the server sets when a handoff is redeemed, so the page bundle still knows to ask about an
 * editing session after the frame has navigated away from the handoff URL -- signing in, for one.
 *
 * It holds a literal "1". Nothing is trusted because it is present: it only decides whether to make
 * the server call that actually settles the question against an HttpOnly cookie this cannot read.
 */
export const DESIGNER_FRAME_COOKIE_NAME = "s1ContEditFrame";

/**
 * Whether this document is the page inside the visual content designer's frame.
 *
 * Deliberately cheap, and deliberately not authorization. Both halves are forgeable by whoever is
 * asking -- a query parameter any site can put on a URL, and a cookie any script on this origin can
 * write -- so this is only ever allowed to suppress behaviour that would break the designer, never to
 * grant access to anything. What it gates is a question put to the server, not an answer.
 */
export const isContentDesignerFrame = (): boolean => {
  if (typeof window === "undefined" || window.parent === window) {
    return false;
  }

  try {
    //Either half is enough. The flag covers the first load, the cookie covers every page the frame
    //is navigated to afterwards, where the flag is no longer on the address.
    if (new URLSearchParams(window.location.search).get(DESIGNER_QUERY_FLAG) === "1") {
      return true;
    }
    return document.cookie.split(";").some((c) => c.trim().startsWith(`${DESIGNER_FRAME_COOKIE_NAME}=`));
  } catch {
    return false;
  }
};

export type DesignerMessageType =
  | "designer:handshake"
  | "designer:set-mode"
  | "designer:set-preview"
  | "designer:clear-preview"
  | "designer:verify-selector"
  | "designer:scroll-to-selector"
  | "designer:upload-asset"
  | "designer:asset-uploaded"
  | "designer:set-edit-mode"
  | "designer:set-content"
  | "designer:content-changed"
  | "designer:edit-mode-changed"
  | "designer:teardown"
  | "designer:ready"
  | "designer:picked"
  | "designer:pick-cancelled"
  | "designer:selector-verified"
  | "designer:preview-applied"
  | "designer:navigated"
  | "designer:error";

export interface IDesignerEnvelope<TPayload = unknown> {
  source: typeof DESIGNER_MESSAGE_SOURCE;
  v: typeof DESIGNER_PROTOCOL_VERSION;
  type: DesignerMessageType;
  payload: TPayload;
}

export interface IDesignerHandshakePayload {
  designerOrigin: string;
}

export interface IDesignerSetModePayload {
  mode: "idle" | "picking";
  /**
   * Let the pick reach inside content the designer itself put on the page. Optional, so a designer that
   * does not send it and a page that does not read it still agree on DESIGNER_PROTOCOL_VERSION.
   */
  includePlacedContent?: boolean;
}

export interface IDesignerSetPreviewPayload {
  previewKey: string;
  selector: string;
  matchText?: string;
  position: ContentInsertPosition;
  html: string;
  label: string;
  /** The mapping this preview is a draft of, so its published copy can stand aside while it shows. */
  pageContentMapId?: number;
  /**
   * Registry key of a component being placed instead of html. Optional, so a designer that does not
   * send it and a page that does not read it still agree on DESIGNER_PROTOCOL_VERSION.
   */
  componentKey?: string;
  /** Json the component's declared fields were filled in with. */
  componentProps?: string;
  /** Json mapping the component's publishable events to the page keys they write. */
  componentEmits?: string;
  /**
   * Source of a bundled component being written, instead of html or a placed component. Optional for
   * the same reason as componentKey: a designer that does not send it and a page that does not read it
   * still agree on DESIGNER_PROTOCOL_VERSION.
   */
  authoredSource?: string;
}

export interface IDesignerClearPreviewPayload {
  previewKey: string;
}

export interface IDesignerSetEditModePayload {
  previewKey: string;
  isEditing: boolean;
}

export interface IDesignerSetContentPayload {
  previewKey: string;
  html: string;
}

export interface IDesignerContentChangedPayload {
  previewKey: string;
  html: string;
}

export interface IDesignerEditModeChangedPayload {
  previewKey: string;
  isEditing: boolean;
  error?: string;
}

export interface IDesignerVerifySelectorPayload {
  requestId: string;
  selector: string;
  matchText?: string;
}

export interface IDesignerScrollToSelectorPayload {
  selector: string;
  matchText?: string;
}

/**
 * What the framed page says about itself.
 *
 * Only the path travels, and deliberately so. A page id would be an identifier minted on a public
 * origin that the designer then wrote rows against, so the designer resolves the path against its own
 * authenticated page list instead. The full href and the document title are withheld for the same
 * reason in reverse: once a content manager can sign in inside the frame, both start carrying the
 * visitor's data -- auth tokens in the query, an account holder's name in the title.
 */
/** The only image types the designer will store on a framed page's behalf. */
export const DESIGNER_UPLOAD_MIME_TYPES: readonly string[] = ["image/png", "image/jpeg", "image/webp"];

/**
 * Hard cap on the data url that may cross the bridge. Base64 runs about a third larger than the file,
 * so this admits roughly a 6MB image. It is a bound on what the frame can make the designer buffer
 * and post, not a statement about what the asset api accepts.
 */
export const DESIGNER_UPLOAD_MAX_DATA_LENGTH = 8 * 1024 * 1024;

/**
 * An image the in-page editor wants stored, handed to the designer because the framed origin has no
 * session to store it with.
 *
 * Deliberately four plain fields rather than an asset record. The frame is a public origin and this
 * payload ends up driving an authenticated call on the designer's side, so the frame supplies only
 * the bytes and a suggested name -- every other property of the stored asset is the designer's to
 * decide, and none of these are trusted without checking.
 */
export interface IDesignerUploadAssetPayload {
  requestId: string;
  fileName: string;
  mime: string;
  /** Data url, as produced by FileReader.readAsDataURL. */
  data: string;
}

export interface IDesignerAssetUploadedPayload {
  requestId: string;
  /** Where the stored image can be read from, when it stored. */
  url?: string;
  error?: string;
}

export interface IDesignerReadyPayload {
  route: string;
}

export interface IDesignerPickedPayload {
  selector: string;
  strategy: ElementSelectorStrategy;
  matchText: string;
  humanLabel: string;
  isLikelyUnstable: boolean;
  tagName: string;
}

export interface IDesignerSelectorVerifiedPayload {
  requestId: string;
  isValidSyntax: boolean;
  matchCount: number;
  isUnique: boolean;
  humanLabel: string;
  /**
   * Whether the element sits in the author-created page's own content mount. An anchor in there is
   * only rendered because an unanchored placement put it there, so a placement that anchors to it
   * leaves the mount and takes the element it named with it -- which renders the page empty.
   */
  isInsidePageContent: boolean;
}

export interface IDesignerPreviewAppliedPayload {
  previewKey: string;
  isApplied: boolean;
  matchCount: number;
}

export interface IDesignerNavigatedPayload {
  route: string;
}

export interface IDesignerErrorPayload {
  code: "no-session" | "bad-origin" | "invalid-selector" | "not-anchorable";
  message: string;
}

/**
 * Builds a protocol envelope. Both ends check the envelope before they look at the message type.
 * @param type
 * @param payload
 */
export const buildDesignerMessage = <TPayload>(
  type: DesignerMessageType,
  payload: TPayload
): IDesignerEnvelope<TPayload> => ({
  source: DESIGNER_MESSAGE_SOURCE,
  v: DESIGNER_PROTOCOL_VERSION,
  type,
  payload,
});

/**
 * Whether a received value is a designer envelope this build speaks.
 * @param data
 */
export const isDesignerMessage = (data: unknown): data is IDesignerEnvelope => {
  const envelope = data as IDesignerEnvelope | null;
  return (
    !!envelope &&
    typeof envelope === "object" &&
    envelope.source === DESIGNER_MESSAGE_SOURCE &&
    envelope.v === DESIGNER_PROTOCOL_VERSION &&
    typeof envelope.type === "string"
  );
};

/**
 * The origin of a URL string, or empty when it is not a parseable absolute URL.
 * Only the origin survives, so a path or query appended by a caller is discarded.
 * @param value
 */
export const parseOrigin = (value: string): string => {
  try {
    return new URL(value).origin;
  } catch {
    return "";
  }
};
